import { Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FunctionComponent, ReactNode } from "react";
import styles from "./searchHelp.module.css";

// Which set of rules to show. They differ only where the surface differs:
// - "global"     the top-bar search modal: three groups, capped, parts included
// - "table"      the order, quote and basket tables: one list, no cap
// - "customer"   the customer overview: no ids to pin, no parts
// - "production" the Production Overview: rows are parts, so there is no
//                order to pin a "#" to and nothing to say about models that
//                were never ordered - every row already is one
export type SearchHelpVariant = "global" | "table" | "customer" | "production";

// Mirrors CAP_PER_GROUP in the backend SearchController. Quoted in the
// tooltip only - the section headers get the real numbers from the
// response ("5 of 173"), so drift here misleads the tip, it does not
// break a count.
const HITS_PER_GROUP = 5;

const Example: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <span className={styles.example}>{children}</span>
);

// Plain-language version of the matching rules in backend utils/search.py.
// Written for people who do not think in queries, so it leads with the two
// behaviours that otherwise read as bugs: adding a word NARROWS the result
// (tokens are ANDed), and a model that was never ordered cannot be found
// (a part is an order/quote line, not a product row).
const tips = (variant: SearchHelpVariant): ReactNode[] => {
  const fields =
    variant === "customer"
      ? "customer name, e-mail, company, VAT or phone"
      : variant === "production"
        ? "model name, part number, order number, customer, company, e-mail, phone, VAT or PO number"
        : "order or quote number, model name, customer, company, e-mail, phone, VAT or PO number";

  const rules: ReactNode[] = [
    <>
      Type any part of what you are looking for - {fields}. Upper/lower case
      makes no difference.
    </>,
    <>
      Several words all have to match, in any order:{" "}
      <Example>acme bracket</Example> finds only rows that contain both.
    </>,
    <>
      Numbers match anywhere in an id, so <Example>414</Example> also finds
      4146.
    </>,
  ];

  if (variant === "production") {
    rules.push(
      <>
        A leading <Example>#</Example> is ignored, so <Example>#4146</Example>
        {" "}and <Example>4146</Example> find the same part.
      </>
    );
  } else if (variant !== "customer") {
    rules.push(
      <>
        Put <Example>#</Example> in front of a number to pin the list to that
        one order or quote, e.g. <Example>#1234</Example>.
      </>,
      <>
        Parts are found through the orders and baskets they sit on - a model
        that has never been ordered or quoted will not appear.
      </>
    );
  }

  if (variant === "global") {
    rules.push(
      <>
        Each group shows the {HITS_PER_GROUP} best matches; use <b>See all</b>{" "}
        for the rest.
      </>
    );
  }

  return rules;
};

type PropsType = {
  variant: SearchHelpVariant;
};

const SearchHelp: FunctionComponent<PropsType> = ({ variant }) => (
  // Hover, or keyboard focus - MUI opens a Tooltip on focus too. The rules
  // are reference material, not a page, so there is nothing to click through
  // to. enterTouchDelay 0 / a long leaveTouchDelay make it usable on touch,
  // where there is no hover at all.
  <Tooltip
    title={
      <div className={styles.tooltip}>
        <div className={styles.title}>How to search</div>
        <ul className={styles.list}>
          {tips(variant).map((rule, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{rule}</li>
          ))}
        </ul>
      </div>
    }
    arrow
    placement="bottom-end"
    enterTouchDelay={0}
    leaveTouchDelay={10000}
    componentsProps={{ tooltip: { className: styles.tooltipRoot } }}
  >
    {/* A button, not an <a>: there is nowhere to navigate to, and an
        href-less anchor is not focusable. Styled as a link. */}
    <button type="button" className={styles.help} aria-label="Search help">
      <HelpOutlineIcon className={styles.icon} fontSize="inherit" />
      Help
    </button>
  </Tooltip>
);

export default SearchHelp;
